import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ActivityIndicator, LayoutChangeEvent, RefreshControl, StyleSheet, useWindowDimensions } from 'react-native';
import Animated, { useAnimatedScrollHandler, useAnimatedStyle, useSharedValue } from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { useNavigation, useScrollToTop } from '@react-navigation/native';

import { Screen } from '@/components/ScreenComponent';
import { SizedBox } from '@/components/SizedBox';
import { BOTTOM_TABS_HEIGHT } from '@/components/TabBar';
import { Text } from '@/components/TextComponent';
import { Box } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { MaxWidthWrapper } from '@/feature/responsive-design/WebComponents';
import { useSocialFeedAndEventsData } from '@/feature/social-feed/use-social-feed';
import { useForceRefresh } from '@/hooks/use-force-refresh';
import { useShowOneTime } from '@/hooks/use-show-one-time';
import { useUserStore } from '@/hooks/use-user-store';
import { common, designSystem } from '@/styles/styles';
import { SocialFeedEntry } from '@/types/socialFeed.generated';
import { showScrollIndicator } from '@/utils/constants-platform-specific';
import { FlashList } from '@shopify/flash-list';

import { useResponsiveColumnCount } from '../responsive-design/hooks/use-responsive-column-count';
import { SocialFeedCard } from './SocialFeedCard';
import { EditUsernameAvatarBanner } from './components/EditUsernameAvatarBanner';
import { EmptyFeed } from './components/EmptyFeed';

const AnimatedFlashList = Animated.createAnimatedComponent(FlashList<SocialFeedEntry>);

const FEED_HEADER_HEIGHT = 56;

export const SocialFeedScreen = () => {
    const columnCount = useResponsiveColumnCount([1, 1, 1, 2, 3]);

    const { data: socialFeedData, refetch, fetching } = useSocialFeedAndEventsData({ enabled: false });

    const renderItem = useCallback(
        ({ item, index }: { item: SocialFeedEntry; index: number }) => {
            const columnIndex = index % columnCount;
            const isFirstColumn = columnIndex === 0;
            const isLastColumn = columnIndex === columnCount - 1;

            return (
                <Box flex={1} paddingLeft={isFirstColumn ? 's0' : 's8'} paddingRight={isLastColumn ? 's0' : 's8'}>
                    <SocialFeedCard feedEntry={item} />
                </Box>
            );
        },
        [columnCount]
    );

    const refresh = useCallback(() => {
        return refetch();
    }, [refetch]);

    const listRef = useRef<FlashList<SocialFeedEntry>>(null);
    const scrollY = useSharedValue(0);

    const onScroll = useAnimatedScrollHandler({
        onScroll: event => {
            scrollY.value = event.contentOffset.y;
        },
    });
    useScrollToTop(listRef);

    const headerStyle = useAnimatedStyle(() => {
        const y = scrollY.value;
        return {
            transform: [{ translateY: y < 0 ? y : 0 }],
        };
    });

    const [headerHeight, setHeaderHeight] = useState(0);

    const onHeaderLayout = useCallback(
        (event: LayoutChangeEvent) => {
            const { height } = event.nativeEvent.layout;
            setHeaderHeight(height);
        },
        [setHeaderHeight]
    );

    const { forceRefreshing, onRefresh } = useForceRefresh(refresh);

    const navigation = useNavigation();
    const guest = useUserStore(state => state.guest);
    const { show: showEditUserBanner, handleInteraction: handleBannerInteraction } = useShowOneTime(
        'SocialFeed_EditUsernameAvatarBanner'
    );

    const handleBannerEdit = useCallback(() => {
        navigation.navigate('EditUsernameAndAvatar');
    }, [navigation]);

    const { height } = useWindowDimensions();
    const insets = useSafeAreaInsets();

    const EmptyFeedComponent = useMemo(() => {
        const pageHeight = height - insets.top - insets.bottom - FEED_HEADER_HEIGHT - BOTTOM_TABS_HEIGHT;
        return <EmptyFeed pageHeight={pageHeight} />;
    }, [height, insets]);

    useEffect(() => {
        refresh();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <MaxWidthWrapper flex={1}>
            <Screen edges={['top']} style={common.flex}>
                <Box flex={1}>
                    {fetching ? (
                        <Box flex={1} justifyContent={'center'}>
                            <ActivityIndicator />
                        </Box>
                    ) : (
                        <AnimatedFlashList
                            onScroll={onScroll}
                            ref={listRef}
                            data={socialFeedData}
                            numColumns={columnCount}
                            renderItem={renderItem}
                            ListEmptyComponent={EmptyFeedComponent}
                            // we have 3 different card heights: 2 picks, 3 picks, 4+ picks
                            // so we use the 3 picks card height as the average
                            estimatedItemSize={402}
                            ItemSeparatorComponent={ItemSeparator}
                            showsVerticalScrollIndicator={showScrollIndicator}
                            contentContainerStyle={styles.list}
                            keyExtractor={(item, index) => `${index}-${item.createdDate}`}
                            refreshControl={
                                socialFeedData ? (
                                    <RefreshControl
                                        refreshing={forceRefreshing}
                                        onRefresh={onRefresh}
                                        colors={[designSystem.colors.neutral700]}
                                        tintColor={ds.colorValue.icon.primary}
                                        progressViewOffset={headerHeight}
                                    />
                                ) : undefined
                            }
                            ListHeaderComponent={
                                <Animated.View style={headerStyle} onLayout={onHeaderLayout}>
                                    <Box paddingTop={'s12'} paddingBottom={'s12'}>
                                        <Text variant={'headlineMedium'} fontSize={24} letterSpacing={0.07}>
                                            Feed
                                        </Text>
                                    </Box>
                                    {showEditUserBanner && !guest ? (
                                        <EditUsernameAvatarBanner
                                            onEdit={handleBannerEdit}
                                            onDismiss={handleBannerInteraction}
                                        />
                                    ) : null}
                                </Animated.View>
                            }
                        />
                    )}
                </Box>
            </Screen>
        </MaxWidthWrapper>
    );
};

const ItemSeparator = () => <SizedBox value={16} />;

const styles = StyleSheet.create({
    list: {
        paddingBottom: 40,
        paddingTop: 12,
        paddingHorizontal: 16,
    },
});
